import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '../utils/api';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const Icon = ({ name, size = 18, color = 'currentColor' }) => {
  const icons = {
    users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    eye: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
    download: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>,
    shield: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    file: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
    zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    ban: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>,
    chart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
    checkCircle: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    arrowRight: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>,
    globe: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
    refresh: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
  };
  return icons[name] || null;
};

const C = {
  bg: '#0a0a0a',
  surface: '#141414',
  surface2: '#1a1a1a',
  border: '#222',
  text: '#fff',
  textMuted: '#777',
  textSub: '#aaa',
  accent: '#d4f535',
  green: '#4ade80',
  orange: '#fb923c',
  red: '#f87171',
  purple: '#a78bfa',
};

function StatCard({ icon, label, value, sub, color, danger, accentColor }) {
  const col = accentColor || color || C.purple;
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${danger ? '#3f1515' : C.border}`,
      borderRadius: 12, padding: '16px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: col + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={icon} size={18} color={col} />
        </div>
        <Icon name="arrowRight" size={14} color={C.textMuted} />
      </div>
      <div style={{ fontSize: 30, fontWeight: 900, color: col, lineHeight: 1, marginBottom: 4, letterSpacing: '-1px' }}>{value}</div>
      <div style={{ fontSize: 13, color: C.text, fontWeight: 600, marginBottom: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.textSub }}>{sub}</div>}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: col + '40' }} />
    </div>
  );
}

function EventRow({ e }) {
  const config = {
    open:               { icon: 'file',   color: C.purple, label: 'a ouvert' },
    download:           { icon: 'download', color: C.orange, label: 'a téléchargé' },
    unauthorized_access:{ icon: 'ban',    color: C.red,    label: 'accès refusé' },
    link_shared:        { icon: 'zap',    color: C.accent, label: 'lien partagé' },
  };
  const cfg = config[e.event_type] || { icon: 'file', color: C.textSub, label: e.event_type };
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 16px', borderBottom: `1px solid ${C.border}`,
      background: e.event_type === 'download' ? '#1f1208' : 'transparent',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: cfg.color + '15',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={cfg.icon} size={14} color={cfg.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {e.member_name} <span style={{ color: C.textSub, fontWeight: 400 }}>{cfg.label}</span>
        </div>
        <div style={{ fontSize: 10, color: C.textMuted, display: 'flex', gap: 6, marginTop: 2 }}>
          {e.country && <span><Icon name="globe" size={10} color={C.textMuted} /> {e.city || e.country}</span>}
          {e.browser && <span>· {e.browser}</span>}
        </div>
      </div>
      <div style={{ fontSize: 10, color: C.textMuted, flexShrink: 0 }}>
        {format(new Date(e.created_at), 'HH:mm')}
      </div>
    </div>
  );
}

function ReadingChart({ members }) {
  const getStatus = (m) => {
    const t = m.total_reading_time || 0;
    const opens = m.total_opens || 0;
    if (opens === 0 || t === 0) return { status: 'Non lu', col: C.textSub, barWidth: 0, timeLabel: '—' };
    if (t < 30) return { status: 'Lecture rapide', col: C.red, barWidth: Math.min((t / 30) * 30, 30), timeLabel: t + 's' };
    if (t < 120) return { status: 'Lecture partielle', col: C.orange, barWidth: 30 + Math.min(((t - 30) / 90) * 35, 35), timeLabel: t < 60 ? t + 's' : Math.floor(t / 60) + 'min' + (t % 60 > 0 ? ' ' + (t % 60) + 's' : '') };
    return { status: 'Lecture complète', col: C.green, barWidth: 65 + Math.min((t / 600) * 35, 35), timeLabel: Math.floor(t / 60) + 'min' + (t % 60 > 0 ? ' ' + (t % 60) + 's' : '') };
  };
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon name="chart" size={16} color={C.accent} />
        <h2 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>Statut de lecture par membre</h2>
      </div>
      <p style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>Basé sur le temps de lecture enregistré</p>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        {[{col: C.green, label:'Complète'},{col: C.orange, label:'Partielle'},{col: C.red, label:'Rapide'},{col: C.textSub, label:'Non lu'}].map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.col }} />
            <span style={{ fontSize: 11, color: C.textSub }}>{l.label}</span>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {members.map((m, i) => {
          const { status, col, barWidth, timeLabel } = getStatus(m);
          return (
            <div key={i} style={{ background: C.surface2, border: `1px solid ${col}30`, borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: col, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.name}</div>
                    <div style={{ fontSize: 10, color: C.textMuted }}>{m.email}</div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: col }}>{timeLabel}</div>
                  <div style={{ fontSize: 10, color: col, fontWeight: 600 }}>{status}</div>
                </div>
              </div>
              <div style={{ background: '#222', borderRadius: 999, height: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 999, background: col, width: barWidth + '%', transition: 'width 0.5s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <span style={{ fontSize: 10, color: C.textMuted }}>{m.total_opens || 0} ouverture{(m.total_opens || 0) > 1 ? 's' : ''}</span>
                <span style={{ fontSize: 10, color: C.textMuted }}>{m.last_open_at ? 'Dernier accès: ' + new Date(m.last_open_at).toLocaleDateString('fr-FR') : 'Jamais ouvert'}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DashboardHome() {
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchData = useCallback(async () => {
    try {
      const [s, m] = await Promise.all([api.get('/track/stats'), api.get('/members')]);
      setStats(s.data); setMembers(m.data);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 15000);
    return () => clearInterval(t);
  }, [fetchData]);

  if (loading) return (
    <div style={{ padding: 32, color: C.textSub, fontSize: 14, background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Icon name="refresh" size={16} color={C.accent} />
        <span style={{ color: C.textSub }}>Chargement...</span>
      </div>
    </div>
  );

  const groupStats = [
    { name: 'Confiant',       value: members.filter(m => m.grp === 'confiant').length },
    { name: 'Moins confiant', value: members.filter(m => m.grp === 'moins_confiant').length },
    { name: 'Pas confiant',   value: members.filter(m => m.grp === 'pas_confiant').length },
  ];
  const COLORS = [C.green, C.orange, C.red];

  return (
    <div style={{ padding: 16, background: C.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>
          ADMINISTRATION / VUE D'ENSEMBLE
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: 0, letterSpacing: '-0.5px' }}>Vue d'ensemble</h1>
        <p style={{ color: C.textMuted, fontSize: 11, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.accent, display: 'inline-block' }} />
          Auto-refresh 15s · {format(new Date(), "d MMM yyyy HH:mm", { locale: fr })}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
        <StatCard icon="users"    label="Membres"        value={stats?.totalMembers || 0}      accentColor={C.purple} sub={`${stats?.membersOpened || 0} ont ouvert un document`} />
        <StatCard icon="eye"      label="Ouvertures"     value={stats?.totalOpens || 0}         accentColor={C.green}  sub={`${stats?.membersOpened || 0} lecteurs distincts`} />
        <StatCard icon="download" label="Téléchargements" value={stats?.totalDownloads || 0}   accentColor={C.orange} danger={stats?.totalDownloads > 0} sub="Document téléchargé" />
        <StatCard icon="ban"      label="Accès refusés"  value={stats?.totalUnauthorized || 0}  accentColor={C.red}    danger={stats?.totalUnauthorized > 0} sub="Tentatives bloquées" />
      </div>


      {/* Activité récente */}
      {stats?.recentEvents?.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="zap" size={16} color={C.accent} />
            <h2 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>Activité récente</h2>
          </div>
          {stats.recentEvents.slice(0, 8).map((e, i) => <EventRow key={i} e={e} />)}
        </div>
      )}

      {/* Résumé statut lecture */}
      {members.length > 0 && (() => {
        const complet = members.filter(m => (m.total_reading_time||0) >= 120).length;
        const partiel = members.filter(m => { const t=m.total_reading_time||0; return t>=30&&t<120; }).length;
        const rapide  = members.filter(m => { const t=m.total_reading_time||0; return t>0&&t<30; }).length;
        const nonLu   = members.filter(m => !m.total_reading_time||m.total_reading_time===0).length;
        return (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Icon name="checkCircle" size={16} color={C.accent} />
              <h2 style={{ fontSize: 14, fontWeight: 700, color: C.text, margin: 0 }}>Statut de lecture — {members.length} membres</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
              {[
                { label: 'Lecture complète', count: complet, color: C.green },
                { label: 'Partielle',        count: partiel, color: C.orange },
                { label: 'Rapide',           count: rapide,  color: C.red },
                { label: 'Non lu',           count: nonLu,   color: C.textSub },
              ].map((s, i) => (
                <div key={i} style={{ background: C.surface2, border: `1px solid ${s.color}25`, borderRadius: 10, padding: '12px', textAlign: 'center' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, margin: '0 auto 8px' }} />
                  <div style={{ fontSize: 28, fontWeight: 900, color: s.color, lineHeight: 1, letterSpacing: '-1px' }}>{s.count}</div>
                  <div style={{ fontSize: 10, color: s.color, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 9, color: C.textMuted, marginTop: 2 }}>
                    {members.length > 0 ? Math.round(s.count/members.length*100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Graphique lecture */}
      {members.length > 0 && <ReadingChart members={members} />}
    </div>
  );
}
